import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  TimeIntervalAddForm,
  TimeIntervalAddInitialValues,
  TimeIntervalComponents,
  TimeIntervalAdditionalParams,
  TimeIntervalFormOptions,
  onTimeIntervalFormSaved,
  ITimeIntervalFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateTimeInterval, PermissionEnum, } from 'atlas-shared';
import { createTimeInterval, fetchTimeInterval, getTimeIntervalCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateTimeInterval> {}

const TimeIntervalFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreateTimeInterval>>();

  const param_props: ITimeIntervalFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout = useMemo(() => TimeIntervalAddForm(values, param_props, t), [values, organizations, t, abilities, ]);
  const options: IFormOptions = useMemo(() => TimeIntervalFormOptions(values || TimeIntervalAddInitialValues, param_props, t), [values, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateTimeInterval, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateTimeInterval) => save(() => createTimeInterval(values).then(
    res => onSaved ? onSaved(res, navigate) : onTimeIntervalFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchTimeInterval(+id)
        .then(({
          organization_id,
          title,
          interval,
          open,
        }) => {
          setCloned({
            organization_id,
            title,
            interval,
            open,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.TimeInterval, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={TimeIntervalComponents}
        additionalParams={TimeIntervalAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...TimeIntervalAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('TIME_INTERVAL')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const TimeIntervalFormAddView = React.memo(withAdd(TimeIntervalFormAdd, getTimeIntervalCreateSchema, TimeIntervalAddInitialValues));
